import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { Card, CardBody } from 'reactstrap'

import { selectCsvUploaderStatus, getBuckets, uploadCsvFile } from 'slices/csvUploaderSlice'
import { showError, showSuccess } from 'slices/notificationSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES } from 'slices/utils'

import { CardSubmitFooter, FileInput, SelectBoxFormat } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'

import { useImportCsv } from 'hooks/useImportCsv'
import type { LoadFilePropsWithoutEncoding } from 'hooks/useImportCsv'

const ENCODING_ERROR_MESSAGE = 'SHIFT-JIS形式のファイルを選択してください。'

/**
 * ファイルがSHIFT-JIS以外と判定できる場合に true を返す。
 * - UTF-8 BOM / UTF-16 BOM が存在する場合は確実にNG
 * - UTF-8として厳密デコードに成功し、かつ非ASCIIバイトがある場合はUTF-8と判定
 * 100%の検出は不可能なため、ベストエフォートの検出。
 */
const detectNonShiftJis = (buffer: ArrayBuffer): boolean => {
  const bytes = new Uint8Array(buffer)

  // UTF-8 BOM (EF BB BF)
  if (bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf) {
    return true
  }

  // UTF-16 BOM (FF FE = LE, FE FF = BE)
  if ((bytes[0] === 0xff && bytes[1] === 0xfe) || (bytes[0] === 0xfe && bytes[1] === 0xff)) {
    return true
  }

  // UTF-8として厳密デコードを試みる
  try {
    new TextDecoder('utf-8', { fatal: true }).decode(buffer)
    // デコード成功 かつ 非ASCIIバイトあり → UTF-8の可能性が高い
    const hasNonAscii = bytes.some(b => b > 0x7f)
    return hasNonAscii
  } catch {
    // デコード失敗 → SHIFT-JIS等の可能性あり
    return false
  }
}

export const CsvAutoRecordsUploadTab = () => {
  const [selectedBucketDirectory, setSelectedBucketDirectory] = useState<string>()
  const [selectedDataSourceName, setSelectedDataSourceName] = useState<string>()
  const [submitted, setSubmitted] = useState(false)
  const [encodingErrorMessage, setEncodingErrorMessage] = useState<string | undefined>(undefined)
  const [fileInputKey, setFileInputKey] = useState(0)

  const dispatch = useDispatch()
  const { buckets, isRequesting, errorMessage } = useSelector(selectCsvUploaderStatus, shallowEqual)

  const { setFile, importDisabled, importCsvFileWithOutEncoding, modalErrorMessage, setModalErrorMessage } =
    useImportCsv()

  const bucketItems = useMemo(() => buckets.map(b => ({ key: b.bucketDirectory, value: b.name })), [buckets])

  const unchanged = !selectedBucketDirectory && importDisabled

  useEffect(() => {
    dispatch(getBuckets())
  }, [dispatch])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(
        showSuccess({
          successMessage: '作業実績のアップロードに成功しました。反映までしばらく時間がかかる場合があります。',
        })
      )
      setSelectedBucketDirectory(undefined)
      setSelectedDataSourceName(undefined)
      setEncodingErrorMessage(undefined)
      setFileInputKey(k => k + 1)
      setFile(null)
    } else if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
      dispatch(showError())
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, dispatch, setFile, setModalErrorMessage])

  const handleFileChange = useCallback(
    (f: File | null) => {
      if (!f) {
        setFile(null)
        return
      }

      const reader = new FileReader()
      reader.onload = e => {
        const buffer = e.target?.result
        if (!(buffer instanceof ArrayBuffer)) {
          setFile(f)
          return
        }
        if (detectNonShiftJis(buffer)) {
          setFile(null)
          setEncodingErrorMessage(ENCODING_ERROR_MESSAGE)
          setFileInputKey(k => k + 1)
        } else {
          setFile(f)
          setEncodingErrorMessage(undefined)
        }
      }
      reader.onerror = () => setFile(f) // 読み取り失敗時はサーバー側に委ねる
      reader.readAsArrayBuffer(f)
    },
    [setFile]
  )

  const onCancel = useCallback(() => {
    setSelectedBucketDirectory(undefined)
    setSelectedDataSourceName(undefined)
    setEncodingErrorMessage(undefined)
    setFileInputKey(k => k + 1)
    setFile(null)
    setModalErrorMessage(undefined)
  }, [setFile, setModalErrorMessage])

  const onSubmit = useCallback(() => {
    if (!selectedBucketDirectory || !selectedDataSourceName) {
      return
    }
    const handleFileLoad = (data: LoadFilePropsWithoutEncoding) => {
      dispatch(uploadCsvFile(selectedBucketDirectory, selectedDataSourceName, data.content))
      setSubmitted(true)
    }
    importCsvFileWithOutEncoding(handleFileLoad)
  }, [dispatch, importCsvFileWithOutEncoding, selectedBucketDirectory, selectedDataSourceName])

  const disabled = importDisabled || !selectedBucketDirectory

  return (
    <div className="mx-3">
      <Card>
        <CardBody className="p-4">
          <div className="d-flex flex-column row-gap-4">
            <div>
              <div className="mb-3">個人別作業実績数を一括でアップロードできる機能です。</div>

              <div className="my-3">
                <div className="my-2 fw-bold font-middle">基本事項</div>
                <ul className="mb-3">
                  <li>この機能を利用するためには事前設定が必要になるため、営業担当者までご連絡ください。</li>
                  <li>
                    アップロードの対象となるのは、「作業管理」で実績入力方法が「自動アップロード」となっている作業のみです。
                  </li>
                </ul>
              </div>

              <div className="my-3">
                <div className="my-2 fw-bold font-middle">データに関する注意事項</div>
                <ul className="mb-3">
                  <li>対応している文字コードはSHIFT-JISのみです。</li>
                  <li>
                    アップロードするCSVデータは、事前設定したデータフォーマットに基づき作成をお願いします。データフォーマットはCSVアップローダーで取り込むデータと同じとなります。
                  </li>
                  <li>事前設定したデータフォーマットと異なるファイルは正しくデータが反映されません。</li>
                  <li>
                    当日を含め、45営業日前までの個人別作業実績数のアップロードが可能です。それ以前の日付や将来日付のデータは反映されません。
                  </li>
                </ul>
              </div>
            </div>
            <div>
              <div className="mb-3">事前に設定されたデータフォーマットでCSVアップロードをしてください。</div>
              <SelectBoxFormat
                label="アップロード先名称"
                placeholder="アップロード先を選択"
                value={selectedBucketDirectory}
                size={COLUMN_SIZES.MIDDLE}
                items={bucketItems}
                onChange={item => {
                  setSelectedBucketDirectory(item.key?.toString())
                  setSelectedDataSourceName(item.value)
                }}
              />
            </div>
            <div>
              <FileInput key={fileInputKey} id="csv-auto-records-upload" accept=".csv" onChange={handleFileChange} />
              {encodingErrorMessage && <div className="text-danger mt-1">{encodingErrorMessage}</div>}
            </div>
            {modalErrorMessage && <div className="text-danger">{modalErrorMessage}</div>}
          </div>
        </CardBody>
        <CardSubmitFooter
          onCancel={onCancel}
          onSubmit={onSubmit}
          submitDisabled={disabled || unchanged}
          cancelDisabled={unchanged}
          submitLabel="アップロード"
          submitName="csv-auto-records-upload-submit"
        />
      </Card>
    </div>
  )
}
